import { useState, Dispatch, SetStateAction, useCallback } from 'react';

/**
 * Drop-in replacement for useState that persists the value in sessionStorage.
 * Restores the saved value on mount, so filters survive route navigation.
 * Uses sessionStorage (tab-scoped), so closing the tab clears the state.
 *
 * @param key   Unique key, namespaced automatically as "erp:<key>"
 * @param initialValue  Used only when no stored value exists
 */
export function useSessionState<T>(
  key: string,
  initialValue: T,
): [T, Dispatch<SetStateAction<T>>] {
  const storageKey = `erp:${key}`;

  const [state, setState] = useState<T>(() => {
    try {
      const stored = sessionStorage.getItem(storageKey);
      return stored !== null ? (JSON.parse(stored) as T) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setSessionState: Dispatch<SetStateAction<T>> = useCallback(
    (value) => {
      setState((prev) => {
        const next =
          typeof value === 'function'
            ? (value as (prev: T) => T)(prev)
            : value;
        try {
          sessionStorage.setItem(storageKey, JSON.stringify(next));
        } catch {}
        return next;
      });
    },
    [storageKey],
  );

  return [state, setSessionState];
}
